import { FC, useState } from 'react';
import { CareMapNotesList } from './note-list/CareMapNotesList';
import { CareMapDrawerPage } from '../components/CareMapDrawerPage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { ICareMapNoteTarget } from './model/ICareMapNoteTarget';
import { CareMapState } from '../caremap/CareMapState';
import { getNoteTrackingContext } from './generic-notes/NotesTrackingHelper';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { CareMapDrawerState } from '../caremap/CareMapDrawerState';
import { useSubscription } from '@meraki-internal/state';
import { NoteCreationChooser } from './add-note/NoteCreationChooser';
import { CaremapNotesModelFactory } from './model/CaremapNotesModelFactory';
import { NotesSortControl, SortField, SortOrder } from './components/NotesSortControl';
import { NotesFilterControl, FilterState } from './components/NotesFilterControl';
import { filterNotes } from './utils/filterNotes';

export type INotesViewProps = {
    noteTarget: ICareMapNoteTarget;
    titlePrefix?: string;
};

export const CareMapNotesView: FC<INotesViewProps> = ({ noteTarget, titlePrefix = '' }) => {

    if (noteTarget.memberId === ''){
        noteTarget.memberId = undefined;
    }

    const drawerState = useInstance(CareMapDrawerState);
    useSubscription(() => drawerState);
    const history = useInstance(HistoryViewModel);
    const cmState = useInstance(CareMapState);
    const tracking = useInstance(MixpanelService);
    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const [startTimestamp, setStartTimestamp] = useState<number>(Date.now());
    const [sortField, setSortField] = useState<SortField>('updatedAt');
    const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
    const [filters, setFilters] = useState<FilterState>({
        noteType: [],
        careCategory: [],
        tags: []
    });

    const caremap = cmState.getActiveCaremap();

    // Guard against accessing caremap before it's loaded
    if (!caremap) {
        return null;
    }

    const section = caremap[noteTarget.section];

    const editNote = (noteId: string) => {
        history.push(`${history.getCurrentLocation().pathname}/${noteId}`);
    };

    const addNote = () => {
        history.push(`${history.getCurrentLocation().pathname}/add-note`);
    };

    let node = section;
    if (noteTarget.memberId) {
        // A pushed note can target a member not yet in the loaded caremap; tolerate the
        // miss rather than throwing on .members.find of an absent member.
        node = (section as any).members?.find((m: any) => m.id === noteTarget.memberId);
    }

    const onDidEnter = () => {
        setStartTimestamp(Date.now());
        drawerState.setIsMaskingFocusedCareMap(true);
    };

    const onWillLeave = () => {
        const timeOnPage = (Date.now() - startTimestamp) / 1000;
        const {location, type} = getNoteTrackingContext({noteTarget, node, note: undefined});
        tracking.trackWithDebounce({
            event: 'Notes List Viewed',
            getContext: () => ({location, type, timeOnPage})
        });
        drawerState.setIsMaskingFocusedCareMap(false);
    };

    const canEdit = caremap.links.self.actions.includes('edit-notes');

    const allNotes = notesFactory.forActiveCaremap().getNotesForTarget(noteTarget);

    const notes = filterNotes(allNotes, filters, '');

    // Check if any filters are active (only noteType and tags in caremap context)
    const hasActiveFilters = filters.noteType.length > 0 || filters.tags.length > 0;

    return (
        <CareMapDrawerPage
            hasBack={true}
            title={`${titlePrefix?.concat(' ')}Notes`}
            enteredHandler={onDidEnter}
            leavingHandler={onWillLeave}
            headerButtons={
                <>
                    {allNotes.length > 0 && (
                        <>
                            <NotesSortControl
                                sortField={sortField}
                                sortOrder={sortOrder}
                                onSortFieldChange={setSortField}
                                onSortOrderChange={setSortOrder}
                                triggerId="sort-trigger-caremap-drawer"
                            />
                            <NotesFilterControl
                                filters={filters}
                                onFiltersChange={setFilters}
                                notes={allNotes}
                                triggerId="filter-trigger-caremap-drawer"
                                hideCareCategory={true}
                            />
                        </>
                    )}
                    <NoteCreationChooser
                        useIcon
                        canAdd={canEdit}
                        onTypeIt={addNote}
                        noteTarget={noteTarget}
                    />
                </>
            }
        >
            <CareMapNotesList
                notes={notes}
                showHeading={false}
                onNoteClicked={editNote}
                onAddNote={addNote}
                data-type="more-notes-list"
                showSortOptions={false}
                sortField={sortField}
                sortOrder={sortOrder}
                hasActiveFilters={hasActiveFilters}
            />
        </CareMapDrawerPage>
    );
};
